"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";

import type { Plan } from "@/types";
import { isUserPremium } from "@/lib/utils";
import type { SubscriptionFrequency } from "@/app/subscription/[frequency]/types";
import { detectUserCurrency } from "@/lib/utils/currency";
import {
	buildPlanGa4Item,
	ga4ViewItemList,
} from "@/lib/analytics/ga4-ecommerce";
import { useSyncMembershipMutation } from "@/features/plan/planInjection";
import { useSyncHostingPlanMutation } from "@/features/order/orderApiInjection";
import { useGetUserQuery } from "@/features/user/userInjection";

import { TabsContent } from "@/components/ui/tabs";
import PlanCard from "@/components/cards/PlanCard";
import PlanButton from "@/components/cards/PlanCard/PlanButton";
import PlanCardsCarouselSkeleton from "@/app/subscription/[frequency]/components/PlanCardsCarouselSkeleton";

type Props = {
	plans: Plan[];
	frequency: SubscriptionFrequency;
	offer?: string;
};

export default function PlanTabItem({ frequency, plans, offer }: Props) {
	const { data: session, status, update } = useSession();
	const [syncMembership] = useSyncMembershipMutation();
	const [syncHostingPlan] = useSyncHostingPlanMutation();
	const [cardsReady, setCardsReady] = useState(false);
	const syncedRef = useRef(false);
	const userId = session?.user?.id ?? "";
	const { refetch } = useGetUserQuery(userId, { skip: userId.length === 0 });
	const currency = detectUserCurrency();
	const isHostingCatalog =
		plans.length > 0 && plans.every((plan) => plan.planType === "hosting");

	useEffect(() => {
		if (status === "loading") return;

		if (status === "unauthenticated" || !session?.user?.id) {
			setCardsReady(true);
			return;
		}

		if (syncedRef.current) return;
		syncedRef.current = true;

		void (async () => {
			try {
				if (isHostingCatalog) {
					await syncHostingPlan().unwrap();
				}
				await syncMembership().unwrap();
				const { data: user } = await refetch();
				if (user) await update({ user });
			} catch {
				// Fall back to the existing session if sync fails.
			} finally {
				setCardsReady(true);
			}
		})();
	}, [
		status,
		session?.user?.id,
		isHostingCatalog,
		syncHostingPlan,
		syncMembership,
		refetch,
		update,
	]);

	const isPremiumUser =
		isUserPremium(session?.user.plan.duration) &&
		plans.some((p) => p._id === session?.user.plan.planID);
	const filteredPlans = isPremiumUser
		? plans.filter((plan) => {
				const priceForFrequency = plan.prices.find((p) => p.duration.toLowerCase() === frequency);
				const priceAmount = priceForFrequency?.amounts?.[currency] ?? priceForFrequency?.amount ?? 0;
				return priceAmount !== 0;
			})
		: plans;

	const filteredPlanIds = filteredPlans.map((plan) => plan._id).join(",");

	useEffect(() => {
		if (!cardsReady || filteredPlans.length === 0) return;
		const listId = isHostingCatalog ? "hosting_plans" : "subscription_plans";
		ga4ViewItemList({
			item_list_id: listId,
			item_list_name: isHostingCatalog ? "Hosting plans" : "Plans",
			items: filteredPlans.map((plan, index) =>
				buildPlanGa4Item(plan, currency, frequency, index + 1),
			),
		});
		// filteredPlans is derived from plans + frequency; ids are the stable signal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cardsReady, currency, filteredPlanIds, frequency, isHostingCatalog]);

	return (
		<TabsContent value={frequency} className="muted-scrollbar grid max-w-full">
			<h1 className="flex h-12 items-center justify-center text-sm text-secondary lg:text-base">
				{offer}
			</h1>
			{cardsReady ? (
				<div className="grid snap-x snap-mandatory scroll-pl-8 grid-flow-col gap-8 overflow-x-auto pb-8 before:w-8 after:w-8 sm:scroll-pl-0 sm:before:hidden sm:after:hidden">
					{filteredPlans.map((plan) => (
						<PlanCard
							key={plan._id ?? plan.name}
							frequency={frequency}
							plan={plan}
							renderPlanButton={
								<PlanButton
									plan={plan}
									plans={filteredPlans}
									frequency={frequency}
									session={session}
									updateSession={update}
								/>
							}
						/>
					))}
				</div>
			) : (
				<PlanCardsCarouselSkeleton />
			)}
		</TabsContent>
	);
}
